import { InfiniteData, QueryKey, keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import Annnotation, { AnnotationStyle } from "~/components/build/Show/components/Annotation";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { fetcher } from "~/components/build/Show/lib/queries";
import { Job } from "~/components/build/Show/lib/types";
import { useReactRouterNavigateHandler } from "~/components/build/Show/lib/useReactRouterNavigateHandler";
import EmptyState from "~/components/shared/EmptyState";
import Spinner from "~/components/shared/Spinner";

type Props = {
  job: Job;
};

type Annotation = {
  id: string;
  body: { html: string | null | undefined };
  style: AnnotationStyle;
  context: string;
};

type AnnotationsData = {
  records: Annotation[];
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
  endCursor: string | null;
};

const useAnnotations = ({ job }: Props) => {
  const { build } = useBuild();

  const queryKey = useMemo(
    () => [
      "build",
      build.number,
      "jobs",
      job.id,
      "annotations",
      `count:${job.statistics.annotationsCount || 0}`,
    ],
    [build.number, job.id, job.statistics.annotationsCount],
  );

  return useInfiniteQuery<
    AnnotationsData,
    Error,
    InfiniteData<AnnotationsData>,
    QueryKey,
    string | null
  >({
    queryKey,
    queryFn: async ({ pageParam }): Promise<AnnotationsData> => {
      const queryParams = new URLSearchParams();
      if (pageParam) {
        queryParams.set("after", pageParam);
      }
      return fetcher<AnnotationsData>(
        `${build.buildDataBasePath}/jobs/${job.id}/annotations?${queryParams.toString()}`,
      );
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => (lastPage.hasNextPage ? lastPage.endCursor : null),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 30, // 30 seconds
  });
};

export default function Annotations({ job }: Props) {
  const navigateClickHandler = useReactRouterNavigateHandler();
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useAnnotations({
    job,
  });
  const { annotationsCount } = job.statistics;

  const handleShowMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  if (!annotationsCount) {
    return (
      <div className="mb-3 flex w-full flex-1 items-center justify-center bg-slate-100">
        <EmptyState
          heading="No annotations"
          subheading={
            <>
              <span>
                There are no annotations for this job. To scope annotations to your job, use the{" "}
              </span>
              <a
                href="https://buildkite.com/docs/agent/v3/cli-annotate"
                target="_blank"
                rel="noopener noreferrer"
                className="monospace"
              >
                buildkite-agent annotate --scope=job
              </a>
              <span> command.</span>
            </>
          }
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="mb-3 flex-1 pt-3">
        <div className="flex w-full items-center space-x-2">
          <Spinner className="h-4 w-4" />
          <span>Fetching annotations...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-3 flex w-full flex-1 flex-col pt-3" onClick={navigateClickHandler}>
      {data?.pages.flatMap((page) => {
        return page.records.map((annotation) => (
          <Annnotation key={annotation.id} annotation={annotation} />
        ));
      })}

      {hasNextPage && (
        <div className="flex w-full items-center justify-center">
          {isFetchingNextPage ? (
            <div className="space-x-2">
              <Spinner className="h-4 w-4" />
              <span>Fetching annotations...</span>
            </div>
          ) : (
            <button className="btn btn-small btn-depth" onClick={handleShowMore}>
              Show more
            </button>
          )}
        </div>
      )}
    </div>
  );
}
